import { useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import i18n from "i18next"
import {
  ArrowRight,
  BarChart3,
  Bell,
  Bot,
  Building2,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  Dumbbell,
  Globe,
  Headphones,
  Instagram,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  Minus,
  Palette,
  Plus,
  Quote,
  Rocket,
  Salad,
  ScanLine,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  UserPlus,
  Users,
  X,
} from "lucide-react"
import { Reveal, useInView } from "@/components/shared/reveal"
import { GrantFooterBand } from "@/components/shared/grant-attribution"
import { CookieConsent } from "@/components/shared/cookie-consent"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { SEOHead } from "@/components/seo-head"
import { initMarketingAnalytics, track, trackPageView } from "@/lib/marketing-analytics"
import { LANGS, translations } from "./main/translations"
import type { Lang, ModuleKey } from "./main/translations"
import {
  AnalyticsVisual,
  AuroraBackdrop,
  BRAND_BLUE,
  BRAND_COLORS,
  BRAND_GREEN,
  BRAND_RED,
  BrandMark,
  BrowserChrome,
  CountUp,
  DietPlannerMock,
  FloatingChip,
  HeroBackdrop,
  KeepHyphenated,
  Marquee,
  PaymentsVisual,
  PhoneMockup,
  PromoMock,
  RotatingWord,
  WhiteLabelVisual,
  WorkoutMock,
} from "./main/visuals"
import { AdminShowcase } from "./main/admin-mock"
import "./main/landing.css"

// ─── Static data ─────────────────────────────────────────────────────────────

const SITE_URL = "https://thenotfit.com"
const OG_IMAGE = `${SITE_URL}/og-image.png`
const SALES_EMAIL = "support@thenotfit.com"
const INSTAGRAM_URL = "https://www.instagram.com/thenotfitapp/"
const PHONE_VIDEO = "https://gyms.fra1.digitaloceanspaces.com/website/flex/flexaccess.mp4"
const LANG_STORAGE_KEY = "tnf-lang"

const MODULES_CONFIG: { id: ModuleKey; price: number; comingSoon?: boolean; icon: typeof Building2 }[] = [
  { id: "business",   price: 149, icon: Building2 },
  { id: "classes",    price: 149, icon: Calendar },
  { id: "workout",    price: 99,  icon: Dumbbell },
  { id: "diet",       price: 99,  icon: Salad },
  { id: "store",      price: 99,  icon: ShoppingBag },
  { id: "whitelabel", price: 99,  icon: Globe },
  { id: "marketing",  price: 49,  icon: Megaphone, comingSoon: true },
]

const LOCATION_PRICE = 49
const OFFER_PRICE = 499

const MARQUEE_ICONS = [ScanLine, Calendar, CreditCard, Salad, Dumbbell, ShoppingBag, Smartphone, BarChart3, Building2, Bell]

/** Bento layout for the six feature cards: which ones are wide and what they show. */
const FEATURE_LAYOUT: { icon: typeof Globe; wide?: boolean; visual?: () => JSX.Element; className?: string }[] = [
  { icon: Globe, wide: true, visual: WhiteLabelVisual },
  { icon: Megaphone },
  { icon: Calendar },
  { icon: CreditCard, wide: true, visual: PaymentsVisual },
  { icon: BarChart3, wide: true, visual: AnalyticsVisual },
  { icon: Bot, className: "md:col-span-2 lg:col-span-1" },
]

const STEP_ICONS = [UserPlus, Palette, Rocket]
const BENEFIT_ICONS = [Salad, Dumbbell, ShieldCheck, BarChart3, Bot, Building2]

const GYMS = [
  { name: "FlexGym Tirana",   instagram: "https://www.instagram.com/flexgym_tirane/",       image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg",    logo: "https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" },
  { name: "Flex Green Coast", instagram: "https://www.instagram.com/flexgymgreencoast.al/", image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/greencoast.jpg", logo: "https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" },
  { name: "Flex Sports Club", instagram: "https://www.instagram.com/flexsportsclub.al/",    image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/classes.jpg",    logo: "https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" },
]

// ─── Language ────────────────────────────────────────────────────────────────

const isLang = (value: string | null | undefined): value is Lang => value === "en" || value === "sq" || value === "it"

/**
 * First visit: ?lang= (so each ad can link its own language) → saved choice → time zone
 * (Albania → sq, Italy → it) → browser language → English. No network lookup.
 */
function detectInitialLang(): Lang {
  try {
    const param = new URLSearchParams(window.location.search).get("lang")
    if (isLang(param)) return param
    const stored = localStorage.getItem(LANG_STORAGE_KEY)
    if (isLang(stored)) return stored
  } catch {}
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (zone === "Europe/Tirane") return "sq"
    if (zone === "Europe/Rome") return "it"
  } catch {}
  for (const tag of navigator.languages || [navigator.language]) {
    const base = tag?.slice(0, 2).toLowerCase()
    if (isLang(base)) return base
  }
  return "en"
}

function langUrl(lang: Lang) {
  return lang === "en" ? `${SITE_URL}/` : `${SITE_URL}/?lang=${lang}`
}

// ─── Small building blocks ───────────────────────────────────────────────────

function LanguagePicker({ lang, setLang, upward = false }: { lang: Lang; setLang: (l: Lang) => void; upward?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = LANGS.find(l => l.code === lang)!

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-background/60 hover:bg-secondary transition-colors select-none text-sm"
        aria-label="Select language"
        aria-expanded={open}
      >
        <Globe className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="font-medium leading-none">{current.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute right-0 w-44 rounded-xl border bg-background shadow-xl overflow-hidden z-50 py-1 ${upward ? "bottom-full mb-2" : "top-full mt-2"}`}>
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false) }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary ${lang === l.code ? "font-semibold" : "font-normal"}`}
            >
              <span className="flex-1 text-left leading-none">{l.label}</span>
              {lang === l.code && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Logo({ size = "md" }: { size?: "md" | "sm" }) {
  const text = size === "md" ? "text-lg" : "text-base"
  const mark = size === "md" ? "w-10 h-10" : "w-9 h-9"
  return (
    <span className="flex items-center gap-1.5">
      <span className={`${text} font-semibold tracking-tight`}>THE</span>
      <img src="/logo.png" alt="THE NOT FIT" className={`${mark} object-contain`} width={40} height={40} />
      <span className={`${text} font-semibold tracking-tight`}>FIT</span>
    </span>
  )
}

function SectionHeading({ eyebrow, title, subtitle, children, invert = false }: { eyebrow: string; title: ReactNode; subtitle?: string; children?: ReactNode; invert?: boolean }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-14">
      <p className="tnf-eyebrow justify-center">
        <BrandMark size={7} />
        {eyebrow}
      </p>
      <h2 className="tnf-display text-balance text-3xl md:text-[2.75rem] font-semibold leading-[1.1] mt-4 mb-4">{title}</h2>
      {subtitle && <p className={`text-lg leading-relaxed ${invert ? "text-white/65" : "text-muted-foreground"}`}>{subtitle}</p>}
      {children}
    </div>
  )
}

/** "Why THE NOT FIT?" with the brand shapes standing in for "NOT". */
function BrandHeading({ text }: { text: string }) {
  const parts = text.split("NOT")
  if (parts.length !== 2) return <>{text}</>
  return (
    <span className="inline-flex items-center justify-center flex-wrap gap-x-3">
      <span>{parts[0].trim()}</span>
      <span className="sr-only">NOT</span>
      <span className="inline-flex items-center gap-[3px]" aria-hidden>
        <span className="inline-block w-[0.6em] h-[0.6em]" style={{ backgroundColor: BRAND_RED }} />
        <span className="inline-block w-[0.6em] h-[0.6em] rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
        <svg className="inline-block w-[0.6em] h-[0.6em]" viewBox="0 0 40 40">
          <polygon points="2,2 38,2 20,38" fill={BRAND_BLUE} />
        </svg>
      </span>
      <span>{parts[1].trim()}</span>
    </span>
  )
}

/** Cross-fading pane; inactive panes stack underneath so the frame keeps the active pane's height. */
function ShowcasePane({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={!active}
      className={`transition-all duration-500 ${active ? "opacity-100 scale-100" : "opacity-0 scale-[0.98] absolute inset-0 pointer-events-none"}`}
    >
      {children}
    </div>
  )
}

function AnnouncementBar({ eyebrow, message, cta }: { eyebrow: string; message: string; cta: string }) {
  return (
    <div className="relative bg-zinc-950 text-white overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-50"
        style={{ background: `linear-gradient(90deg, ${BRAND_RED}40 0%, ${BRAND_GREEN}33 50%, ${BRAND_BLUE}40 100%)` }}
      />
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, ${BRAND_RED}, ${BRAND_GREEN}, ${BRAND_BLUE})` }} />
      <div className="container mx-auto px-4 relative">
        <a href="#offer" className="flex items-center justify-center gap-2 sm:gap-3 py-2 text-xs sm:text-sm group">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 backdrop-blur border border-white/20 font-bold tracking-wider text-[10px] uppercase flex-shrink-0">
            <Clock className="w-3 h-3" strokeWidth={2.5} />
            {eyebrow}
          </span>
          <BrandMark size={8} className="hidden sm:inline-flex" />
          <span className="font-medium truncate">{message}</span>
          <span className="hidden sm:inline-flex items-center gap-1 font-semibold text-white/90 group-hover:text-white transition-colors">
            {cta}
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </a>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function MainLandingPage() {
  const [lang, setLangState] = useState<Lang>(detectInitialLang)
  const [eurOrUsd, setEurOrUsd] = useState<"eur" | "usd">("eur")
  const [selectedModules, setSelectedModules] = useState<Set<ModuleKey>>(new Set(["business"]))
  const [extraLocations, setExtraLocations] = useState(0)
  const [activeShowcaseTab, setActiveShowcaseTab] = useState(0)
  const [showcasePaused, setShowcasePaused] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showStickyCta, setShowStickyCta] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const pricingView = useInView<HTMLDivElement>()

  const t = translations[lang]

  const setLang = (next: Lang) => {
    setLangState(next)
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next)
    } catch {}
  }

  // Keep the app's i18n (signup, login) in the language picked here; Italian has no app translation yet.
  useEffect(() => {
    if (lang !== "it" && i18n.language !== lang) i18n.changeLanguage(lang)
  }, [lang])

  useEffect(() => {
    initMarketingAnalytics()
    trackPageView()
  }, [])

  useEffect(() => {
    if (pricingView.inView) track("ViewContent", { content_name: "pricing", content_category: "pricing" })
  }, [pricingView.inView])

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8)
      setShowStickyCta(window.scrollY > 640)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Albanian shows Lek. EN/IT respect the EUR/USD toggle.
  const currency: "eur" | "usd" | "all" = lang === "sq" ? "all" : eurOrUsd

  // Format a EUR-base price in the active currency.
  // ALL: round to nearest 1,000 (€49 → 5,000 ALL · €499 → 50,000 ALL).
  const fmt = (eur: number): string => {
    if (currency === "all") {
      const all = Math.round((eur * 100) / 1000) * 1000
      return `${all.toLocaleString("en-US")} ALL`
    }
    const sym = currency === "eur" ? "€" : "$"
    return `${sym}${eur}`
  }

  const toggleModule = (id: ModuleKey) => {
    setSelectedModules(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedModules(new Set(MODULES_CONFIG.map(m => m.id)))

  const modulesTotal = MODULES_CONFIG.filter(m => selectedModules.has(m.id)).reduce((sum, m) => sum + m.price, 0)
  const locationsTotal = extraLocations * LOCATION_PRICE
  const grandTotal = modulesTotal + locationsTotal

  const showcaseTabs = [
    { label: t.showcase.tabs.diet,    desc: t.showcase.dietDesc },
    { label: t.showcase.tabs.workout, desc: t.showcase.workoutDesc },
    { label: t.showcase.tabs.promo,   desc: t.showcase.promoDesc, soon: true },
  ]

  const faqItems = t.faq.items.map(item => ({
    q: item.q,
    a: item.a.replace("{offer}", fmt(OFFER_PRICE)).replace("{location}", fmt(LOCATION_PRICE)),
  }))

  const demoHref = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(t.demoSubject)}`
  const onTrialClick = (location: string) => () => track("StartTrialClick", { location, lang })
  const onContactClick = (location: string) => () => track("Contact", { content_name: "demo_request", location, lang })

  const navLinks = [
    { href: "#features",  label: t.nav.features },
    { href: "#showcase",  label: t.nav.showcase },
    { href: "#customers", label: t.nav.customers },
    { href: "#pricing",   label: t.nav.pricing },
    { href: "#faq",       label: t.nav.faq },
  ]

  const jsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "THE NOT FIT",
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      sameAs: [INSTAGRAM_URL],
      contactPoint: { "@type": "ContactPoint", email: SALES_EMAIL, contactType: "customer support", availableLanguage: ["English", "Albanian", "Italian"] },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "THE NOT FIT",
      url: SITE_URL,
      inLanguage: lang,
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "THE NOT FIT",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, iOS, Android",
      description: translations[lang].meta.description,
      url: langUrl(lang),
      image: OG_IMAGE,
      offers: {
        "@type": "Offer",
        price: String(OFFER_PRICE),
        priceCurrency: "EUR",
        description: "All modules, per gym location, per year",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: translations[lang].faq.items.map(item => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a.replace("{offer}", `€${OFFER_PRICE}`).replace("{location}", `€${LOCATION_PRICE}`),
        },
      })),
    },
  ], [lang])

  return (
    <div className="tnf-landing min-h-screen bg-background overflow-x-hidden">
      <SEOHead
        title={t.meta.title}
        description={t.meta.description}
        canonical={langUrl(lang)}
        htmlLang={lang}
        ogLocale={t.meta.ogLocale}
        ogImage={OG_IMAGE}
        ogImageAlt="THE NOT FIT — gym management platform"
        siteName="THE NOT FIT"
        alternates={[
          ...LANGS.map(l => ({ hrefLang: l.code, href: langUrl(l.code) })),
          { hrefLang: "x-default", href: langUrl("en") },
        ]}
        jsonLd={jsonLd}
      />

      <AnnouncementBar eyebrow={t.announcement.eyebrow} message={t.announcement.message} cta={t.announcement.cta} />

      {/* Navigation */}
      <header
        className={`sticky top-0 z-50 transition-colors duration-300 ${
          scrolled || menuOpen ? "border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/65" : "border-b border-transparent"
        }`}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/" aria-label="THE NOT FIT home">
            <Logo />
          </Link>
          <nav className="hidden lg:flex items-center gap-1 text-sm" aria-label="Main">
            {navLinks.map(link => (
              <a key={link.href} href={link.href} className="rounded-lg px-3 py-2 font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors">
                {link.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <LanguagePicker lang={lang} setLang={setLang} />
            </div>
            <Button variant="ghost" asChild className="hidden md:inline-flex">
              <Link to="/login">{t.nav.signIn}</Link>
            </Button>
            <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25">
              <Link to="/signup" onClick={onTrialClick("nav")}>{t.nav.getStarted}</Link>
            </Button>
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              className="lg:hidden grid h-9 w-9 place-items-center rounded-lg border"
              aria-label={t.nav.menu}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t bg-background/95 backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-200">
            <nav className="container mx-auto px-4 py-3 flex flex-col gap-1" aria-label="Mobile">
              {navLinks.map(link => (
                <a key={link.href} href={link.href} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-3 text-base font-medium hover:bg-secondary">
                  {link.label}
                </a>
              ))}
              <div className="flex items-center justify-between gap-3 border-t mt-2 pt-3">
                <LanguagePicker lang={lang} setLang={setLang} />
                <Button variant="outline" asChild>
                  <Link to="/login">{t.nav.signIn}</Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main>
        {/* Hero */}
        <section className="relative isolate overflow-hidden -mt-16" aria-labelledby="hero-heading">
          <HeroBackdrop />
          <div aria-hidden className="absolute inset-0 tnf-grid-dots text-foreground/[0.06] [mask-image:radial-gradient(ellipse_at_top,#000_30%,transparent_70%)]" />
          <div className="container mx-auto px-4 pt-28 md:pt-40 pb-20 md:pb-28 relative">
            <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-14 lg:gap-12 items-center max-w-7xl mx-auto">
              <div className="min-w-0 space-y-7">
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-700 inline-flex items-center gap-2 rounded-full border bg-background/70 backdrop-blur px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
                  <BrandMark size={9} />
                  <span>{t.hero.badge}</span>
                  <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                    <Sparkles className="h-3 w-3" /> AI
                  </span>
                </div>
                <h1
                  id="hero-heading"
                  className="tnf-display text-balance text-[2.5rem] sm:text-6xl lg:text-[3.5rem] xl:text-[4.1rem] font-semibold leading-[1.03] animate-in fade-in slide-in-from-bottom-4 duration-700"
                >
                  <KeepHyphenated text={t.hero.heading} />{" "}
                  <RotatingWord words={t.hero.rotating} srText={t.hero.rotatingSr} wordClassName="tnf-text-gradient-animated pb-[0.12em]" />
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150 fill-mode-both">
                  {t.hero.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-1 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 fill-mode-both">
                  <Button size="lg" asChild className="h-12 px-6 rounded-xl text-base bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/25 transition-transform hover:-translate-y-0.5">
                    <Link to="/signup" onClick={onTrialClick("hero")} className="inline-flex items-center gap-2">
                      {t.hero.cta1}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild className="h-12 px-6 rounded-xl text-base bg-background/70 backdrop-blur transition-transform hover:-translate-y-0.5">
                    <a href={demoHref} onClick={onContactClick("hero")}>
                      <Mail className="w-4 h-4" />
                      {t.hero.cta2}
                    </a>
                  </Button>
                </div>
                <div className="space-y-4 animate-in fade-in duration-700 delay-500 fill-mode-both">
                  <a
                    href="#offer"
                    className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 transition-colors group"
                  >
                    <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
                    <span>{t.hero.offerPill}</span>
                    <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                  </a>
                  <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                    {t.hero.footnote.map((note, i) => (
                      <li key={note} className="inline-flex items-center gap-1.5">
                        <Check className="w-4 h-4" strokeWidth={2.5} style={{ color: BRAND_COLORS[i % 3] }} />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* The admin replica bleeds off the right edge on desktop so it reads at a real size. */}
              <div className="relative min-w-0 animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-200 fill-mode-both lg:w-[118%] xl:w-[126%]">
                <AdminShowcase lang={lang} tenantsLabel={t.hero.tenantsLabel} />
              </div>
            </div>
          </div>
        </section>

        {/* Capability ticker */}
        <section className="border-y bg-secondary/40" aria-label={t.marquee.heading}>
          <div className="py-8">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-5 px-4">{t.marquee.heading}</p>
            <Marquee>
              {t.marquee.items.map((label, i) => {
                const Icon = MARQUEE_ICONS[i % MARQUEE_ICONS.length]
                return (
                  <span key={label} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border bg-background px-4 py-2 text-sm font-medium shadow-sm">
                    <Icon className="h-4 w-4" style={{ color: BRAND_COLORS[i % 3] }} strokeWidth={2} />
                    {label}
                  </span>
                )
              })}
            </Marquee>
          </div>
        </section>

        {/* Features — bento */}
        <section id="features" className="relative py-24 md:py-32 scroll-mt-20">
          <div className="container mx-auto px-4 relative">
            <Reveal>
              <SectionHeading eyebrow={t.features.eyebrow} title={t.features.heading} subtitle={t.features.subheading}>
                <p className="mt-5 inline-flex items-center gap-2 rounded-full border bg-background px-4 py-1.5 text-sm font-medium">
                  <Sparkles className="h-4 w-4 text-primary" />
                  {t.features.valueProp}
                </p>
              </SectionHeading>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {t.features.cards.map((card, i) => {
                const layout = FEATURE_LAYOUT[i]
                const Icon = layout.icon
                const Visual = layout.visual
                const color = BRAND_COLORS[i % 3]
                return (
                  <Reveal key={card.title} delay={(i % 3) * 80} className={`${layout.wide ? "md:col-span-2" : ""} ${layout.className ?? ""}`}>
                    <div
                      className={`group relative h-full overflow-hidden rounded-3xl border bg-card p-6 md:p-7 tnf-lift ${
                        layout.wide ? "lg:grid lg:grid-cols-[1fr_1.15fr] lg:gap-8 lg:items-center" : ""
                      }`}
                    >
                      <div aria-hidden className="absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl opacity-[0.12] transition-opacity group-hover:opacity-25" style={{ backgroundColor: color }} />
                      <div className="relative space-y-4">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:-rotate-3" style={{ backgroundColor: `${color}17` }}>
                          <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.9} />
                        </div>
                        <div>
                          <h3 className="tnf-display font-semibold text-xl">{card.title}</h3>
                          <p className="text-sm font-medium mt-1" style={{ color }}>{card.desc_short}</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{card.desc}</p>
                      </div>
                      {Visual && (
                        <div className="relative mt-6 lg:mt-0">
                          <Visual />
                        </div>
                      )}
                    </div>
                  </Reveal>
                )
              })}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative border-y bg-secondary/40">
          <div className="container mx-auto px-4 py-14">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-10">{t.stats.heading}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 max-w-5xl mx-auto md:divide-x md:divide-border">
              {[
                { Icon: Building2,   value: "10+",     label: t.stats.gyms,          color: BRAND_RED },
                { Icon: Users,       value: "10,000+", label: t.stats.activeMembers, color: BRAND_GREEN },
                { Icon: ShieldCheck, value: "99.9%",   label: t.stats.uptimeSla,     color: BRAND_BLUE },
                { Icon: Headphones,  value: "24/7",    label: t.stats.support,       color: BRAND_RED },
              ].map(({ Icon, value, label, color }) => (
                <div key={label} className="flex flex-col items-center text-center px-4">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.75} />
                  </div>
                  <CountUp value={value} className="tnf-display text-4xl md:text-5xl font-semibold tabular-nums tnf-text-gradient" />
                  <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium mt-2">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Showcase */}
        <section id="showcase" className="relative py-24 md:py-32 scroll-mt-20 overflow-hidden">
          <div className="container mx-auto px-4 relative">
            <Reveal>
              <SectionHeading eyebrow={t.showcase.eyebrow} title={t.showcase.heading} subtitle={t.showcase.subheading} />
            </Reveal>

            <Reveal delay={120}>
              <div
                className="max-w-5xl mx-auto"
                data-paused={showcasePaused}
                onMouseEnter={() => setShowcasePaused(true)}
                onMouseLeave={() => setShowcasePaused(false)}
              >
                <div className="flex justify-center mb-6">
                  <div className="flex flex-wrap justify-center gap-1 rounded-3xl sm:rounded-full border bg-background p-1 shadow-sm" role="tablist">
                    {showcaseTabs.map((tab, idx) => {
                      const isActive = activeShowcaseTab === idx
                      return (
                        <button
                          key={tab.label}
                          role="tab"
                          aria-selected={isActive}
                          onClick={() => setActiveShowcaseTab(idx)}
                          className={`relative overflow-hidden whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            isActive ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {tab.label}
                            {tab.soon && (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full tracking-wider ${isActive ? "bg-background/20" : "bg-secondary"}`}>
                                {t.showcase.comingSoon}
                              </span>
                            )}
                          </span>
                          {isActive && (
                            <span aria-hidden className="absolute inset-x-4 bottom-1 h-0.5 overflow-hidden rounded-full bg-background/20">
                              <span
                                key={activeShowcaseTab}
                                className="tnf-progress block h-full bg-background/80"
                                onAnimationEnd={() => setActiveShowcaseTab(prev => (prev + 1) % showcaseTabs.length)}
                              />
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <p className="text-center text-sm text-muted-foreground mb-8 max-w-2xl mx-auto min-h-[2.5rem]">
                  {showcaseTabs[activeShowcaseTab].desc}
                </p>

                <div className="relative">
                  <div aria-hidden className="absolute -inset-x-10 -top-10 bottom-0 -z-10 rounded-[2.5rem] blur-3xl opacity-30" style={{ background: `linear-gradient(90deg, ${BRAND_RED}, ${BRAND_GREEN}, ${BRAND_BLUE})` }} />
                  <BrowserChrome>
                    <div className="relative">
                      <ShowcasePane active={activeShowcaseTab === 0}><DietPlannerMock themeIdx={0} /></ShowcasePane>
                      <ShowcasePane active={activeShowcaseTab === 1}><WorkoutMock themeIdx={1} /></ShowcasePane>
                      <ShowcasePane active={activeShowcaseTab === 2}><PromoMock comingSoonLabel={t.showcase.comingSoon} /></ShowcasePane>
                    </div>
                  </BrowserChrome>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Case Study */}
        <section id="customers" className="relative py-24 md:py-32 bg-secondary/30 border-y scroll-mt-20">
          <div className="container mx-auto px-4 relative">
            <div className="max-w-6xl mx-auto">
              <Reveal>
                <SectionHeading
                  eyebrow={t.caseStudy.eyebrow}
                  title={<>{t.caseStudy.heading1} <span className="tnf-text-gradient">{t.caseStudy.heading2}</span></>}
                  subtitle={t.caseStudy.description}
                />
              </Reveal>

              <div className="grid md:grid-cols-3 gap-5 mb-8">
                {GYMS.map((gym, i) => {
                  const info = t.caseStudy.gyms[i]
                  return (
                    <Reveal key={gym.name} delay={i * 90}>
                      <Card className="group h-full rounded-3xl overflow-hidden border bg-card tnf-lift">
                        <div className="relative h-56 overflow-hidden bg-zinc-900">
                          <img src={gym.image} alt={gym.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                            <Users className="h-3 w-3" /> {info.members}
                          </span>
                          <div className="absolute bottom-4 left-5 right-5">
                            <h3 className="tnf-display text-white font-semibold text-xl">{gym.name}</h3>
                            <p className="text-white/80 text-sm">{info.subtitle}</p>
                          </div>
                        </div>
                        <CardContent className="p-5 space-y-4">
                          <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
                            {info.features.map(f => (
                              <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Check className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.5} style={{ color: BRAND_COLORS[i % 3] }} />
                                <span className="truncate">{f}</span>
                              </li>
                            ))}
                          </ul>
                          <a
                            href={gym.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors"
                          >
                            <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {t.caseStudy.followInstagram}
                          </a>
                        </CardContent>
                      </Card>
                    </Reveal>
                  )
                })}
              </div>

              <Reveal>
                <div className="relative overflow-hidden rounded-3xl border bg-card p-8 md:p-12 grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
                  <Quote aria-hidden className="absolute -right-6 -top-8 h-44 w-44 rotate-180 text-primary/[0.06]" />
                  <figure className="relative space-y-5">
                    <div className="flex gap-0.5" aria-label="5 out of 5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <blockquote className="tnf-display text-xl md:text-2xl leading-snug font-medium text-foreground">
                      “{t.caseStudy.testimonial}”
                    </blockquote>
                    <figcaption className="flex items-center gap-3 pt-1">
                      <div className="flex-shrink-0 rounded-lg overflow-hidden bg-black p-1.5">
                        <img src={GYMS[0].logo} alt="Flex Gym Group" className="h-7 w-auto object-contain" loading="lazy" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">Flex Gym Group</div>
                        <div className="text-muted-foreground text-xs">{t.caseStudy.operator}</div>
                      </div>
                    </figcaption>
                  </figure>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "3",      color: BRAND_RED },
                      { value: "3,000+", color: BRAND_GREEN },
                      { value: "50+",    color: BRAND_BLUE },
                      { value: "1",      color: BRAND_RED },
                    ].map((stat, i) => (
                      <div key={i} className="text-center p-4 rounded-2xl border bg-background/60">
                        <CountUp value={stat.value} className="tnf-display text-3xl font-semibold tabular-nums" style={{ color: stat.color }} />
                        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mt-1.5">{t.caseStudy.statsLabels[i]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="relative py-24 md:py-32">
          <div className="container mx-auto px-4">
            <Reveal>
              <SectionHeading eyebrow={t.steps.eyebrow} title={t.steps.heading} subtitle={t.steps.subheading} />
            </Reveal>
            <div className="relative grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div aria-hidden className="hidden md:block absolute top-11 left-[16%] right-[16%] h-px" style={{ background: `linear-gradient(90deg, ${BRAND_RED}, ${BRAND_GREEN}, ${BRAND_BLUE})`, opacity: 0.35 }} />
              {t.steps.items.map((step, i) => {
                const Icon = STEP_ICONS[i]
                const color = BRAND_COLORS[i]
                return (
                  <Reveal key={step.title} delay={i * 120} className="relative text-center">
                    <div className="relative mx-auto mb-6 grid h-[5.5rem] w-[5.5rem] place-items-center rounded-3xl border bg-background shadow-lg">
                      <Icon className="h-8 w-8" style={{ color }} strokeWidth={1.75} />
                      <span className="tnf-mono absolute -top-2 -right-2 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ backgroundColor: color }}>
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="tnf-display text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                  </Reveal>
                )
              })}
            </div>
            <Reveal className="mt-12 flex justify-center">
              <Button size="lg" asChild className="h-12 px-6 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/25">
                <Link to="/signup" onClick={onTrialClick("steps")} className="inline-flex items-center gap-2">
                  {t.hero.cta1}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </Reveal>
          </div>
        </section>

        {/* Mobile apps */}
        <section className="relative py-24 md:py-32 bg-secondary/30 border-y overflow-hidden">
          <div className="container mx-auto px-4 relative">
            <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center max-w-6xl mx-auto">
              <Reveal className="relative flex justify-center lg:order-2">
                <div aria-hidden className="absolute inset-10 rounded-full blur-3xl opacity-40" style={{ background: `radial-gradient(circle, ${BRAND_RED}, transparent 60%)` }} />
                <div className="relative">
                  <PhoneMockup videoSrc={PHONE_VIDEO} />
                  <FloatingChip className="-left-24 top-24 hidden sm:block" delay="0.4s">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ backgroundColor: `${BRAND_BLUE}1F`, color: BRAND_BLUE }}>
                        <Calendar className="h-4 w-4" strokeWidth={2.25} />
                      </span>
                      <div className="leading-tight">
                        <div className="text-sm font-semibold">Class booked</div>
                        <div className="tnf-mono text-[11px] text-muted-foreground">HIIT · 18:30</div>
                      </div>
                    </div>
                  </FloatingChip>
                  <FloatingChip className="-right-20 bottom-32 hidden sm:block" delay="1.4s">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ backgroundColor: `${BRAND_RED}1F`, color: BRAND_RED }}>
                        <Bell className="h-4 w-4" strokeWidth={2.25} />
                      </span>
                      <div className="leading-tight">
                        <div className="text-sm font-semibold">Membership renewed</div>
                        <div className="tnf-mono text-[11px] text-muted-foreground">Push · just now</div>
                      </div>
                    </div>
                  </FloatingChip>
                </div>
              </Reveal>
              <Reveal delay={120} className="space-y-6 lg:order-1">
                <p className="tnf-eyebrow"><BrandMark size={7} />{t.mobile.eyebrow}</p>
                <h2 className="tnf-display text-balance text-3xl md:text-[2.75rem] font-semibold leading-[1.1]">{t.mobile.heading}</h2>
                <p className="text-lg text-muted-foreground">{t.mobile.sub}</p>
                <ul className="space-y-3">
                  {[t.mobile.bullet1, t.mobile.bullet2, t.mobile.bullet3].map((b, i) => (
                    <li key={b} className="flex items-start gap-3">
                      <div className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${BRAND_COLORS[i]}1A` }}>
                        <Check className="w-3.5 h-3.5" strokeWidth={3} style={{ color: BRAND_COLORS[i] }} />
                      </div>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 pt-2">
                  {["iOS", "Android", "Web"].map(platform => (
                    <span key={platform} className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-medium">
                      <Smartphone className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
                      {platform}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Why THE NOT FIT — dark */}
        <section className="tnf-dark relative isolate overflow-hidden bg-zinc-950 text-white py-24 md:py-32">
          <AuroraBackdrop className="opacity-40" />
          <div aria-hidden className="absolute inset-0 tnf-grid-dots text-white/[0.07]" />
          <div className="container mx-auto px-4 relative">
            <Reveal>
              <SectionHeading eyebrow={t.benefits.eyebrow} title={<BrandHeading text={t.benefits.heading} />} subtitle={t.benefits.subheading} invert />
            </Reveal>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {t.benefits.items.map((item, i) => {
                const Icon = BENEFIT_ICONS[i]
                const color = BRAND_COLORS[i % 3]
                return (
                  <Reveal key={item.title} delay={(i % 3) * 80}>
                    <div className="h-full rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur transition-colors hover:border-white/25 hover:bg-white/[0.07]">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}33` }}>
                        <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="tnf-display font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-sm text-white/60 leading-relaxed">{item.desc}</p>
                    </div>
                  </Reveal>
                )
              })}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="relative py-24 md:py-32 scroll-mt-20">
          <div className="container mx-auto px-4 relative">
            <Reveal>
              <div ref={pricingView.ref}>
                <SectionHeading eyebrow={t.pricing.eyebrow} title={t.pricing.heading} subtitle={t.pricing.subheading}>
                  {lang !== "sq" && (
                    <div className="flex justify-center pt-6">
                      <div className="inline-flex rounded-full border p-1 bg-background">
                        {(["eur", "usd"] as const).map(c => (
                          <button
                            key={c}
                            onClick={() => setEurOrUsd(c)}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors ${
                              currency === c ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {c === "eur" ? "EUR €" : "USD $"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </SectionHeading>
              </div>
            </Reveal>

            {/* Limited-time offer band — tri-color gradient mesh */}
            <Reveal>
              <div id="offer" className="max-w-5xl mx-auto mb-14 scroll-mt-24">
                <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-zinc-950 text-white shadow-2xl">
                  <div aria-hidden className="absolute inset-0">
                    <div className="absolute -top-32 -left-20 h-[460px] w-[460px] rounded-full blur-[110px]" style={{ backgroundColor: BRAND_RED, opacity: 0.55 }} />
                    <div className="absolute top-1/4 left-1/3 h-[400px] w-[400px] rounded-full blur-[120px]" style={{ backgroundColor: BRAND_GREEN, opacity: 0.45 }} />
                    <div className="absolute -bottom-40 -right-20 h-[500px] w-[500px] rounded-full blur-[110px]" style={{ backgroundColor: BRAND_BLUE, opacity: 0.55 }} />
                  </div>
                  <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute -top-4 right-16 w-32 h-32 rotate-12 mix-blend-screen tnf-float-slow" style={{ backgroundColor: BRAND_RED, opacity: 0.18 }} />
                    <div className="absolute bottom-6 left-12 w-40 h-40 rounded-full mix-blend-screen tnf-float" style={{ backgroundColor: BRAND_GREEN, opacity: 0.16 }} />
                    <svg className="absolute top-12 left-1/2 -translate-x-1/2 w-28 h-28 mix-blend-screen" viewBox="0 0 40 40" style={{ opacity: 0.18 }}>
                      <polygon points="2,2 38,2 20,38" fill={BRAND_BLUE} />
                    </svg>
                    <div className="absolute inset-0 opacity-[0.06] tnf-grid-dots text-white" />
                  </div>
                  <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${BRAND_RED} 0%, ${BRAND_GREEN} 50%, ${BRAND_BLUE} 100%)` }} />

                  <div className="relative p-8 md:p-12 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
                    <div className="space-y-5">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1 text-[10px] font-bold tracking-[0.2em] uppercase">
                        <BrandMark size={9} />
                        <Clock className="w-3 h-3" strokeWidth={2.5} />
                        <span>{t.pricing.offer.eyebrow}</span>
                      </div>
                      <h3 className="tnf-display text-3xl md:text-4xl font-semibold leading-[1.1]">{t.pricing.offer.heading}</h3>
                      <p className="text-sm md:text-base text-white/75 max-w-md leading-relaxed">{t.pricing.offer.subheading}</p>
                      <div className="flex items-center gap-1.5 text-xs text-white/70">
                        <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND_GREEN }} strokeWidth={2} />
                        <span>{t.pricing.offer.compare}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-4">
                      <div className="relative inline-block">
                        <span aria-hidden className="absolute -inset-6 rounded-full blur-2xl opacity-70" style={{ background: `conic-gradient(from 0deg, ${BRAND_RED}, ${BRAND_GREEN}, ${BRAND_BLUE}, ${BRAND_RED})` }} />
                        <span className="relative tnf-display text-6xl md:text-[5.5rem] font-bold tracking-[-0.03em] tabular-nums leading-none">{fmt(OFFER_PRICE)}</span>
                      </div>
                      <span className="text-sm text-white/80 font-medium tracking-wide">{t.pricing.offer.unit}</span>
                      <Button asChild size="lg" className="h-12 px-6 rounded-xl bg-white text-zinc-950 hover:bg-white/90 shadow-lg shadow-black/20 mt-1">
                        <Link to="/signup" onClick={onTrialClick("offer")} className="inline-flex items-center gap-2">
                          {t.pricing.offer.cta}
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto mb-12">
              {MODULES_CONFIG.map((m, i) => {
                const Icon = m.icon
                const mod = t.pricing.modules[m.id]
                const color = BRAND_COLORS[i % 3]
                return (
                  <Reveal key={m.id} delay={(i % 4) * 60}>
                    <Card className="relative h-full rounded-3xl border bg-card tnf-lift">
                      {m.comingSoon && (
                        <span className="absolute -top-2.5 right-4 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-foreground text-background">
                          {t.pricing.comingSoon}
                        </span>
                      )}
                      <CardContent className="p-6 space-y-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}17` }}>
                          <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.75} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-base">{mod.name}</h3>
                          <div className="flex items-baseline gap-1 mt-1">
                            <span className="tnf-display text-3xl font-semibold">{fmt(m.price)}</span>
                            <span className="text-xs text-muted-foreground">{t.pricing.perMonth}</span>
                          </div>
                        </div>
                        <ul className="space-y-1.5 text-sm text-muted-foreground">
                          {mod.features.map(f => (
                            <li key={f} className="flex items-start gap-2">
                              <Check className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" strokeWidth={2.5} />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </Reveal>
                )
              })}

              <Reveal delay={180}>
                <Card className="h-full rounded-3xl border border-dashed bg-card tnf-lift">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-foreground" strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">{t.pricing.extraLocations.name}</h3>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="tnf-display text-3xl font-semibold">{fmt(LOCATION_PRICE)}</span>
                        <span className="text-xs text-muted-foreground">{t.pricing.perMonthEach}</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {t.pricing.extraLocations.features.map(f => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" strokeWidth={2.5} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Reveal>
            </div>

            {/* Pricing Calculator */}
            <Reveal>
              <Card className="max-w-2xl mx-auto rounded-3xl border bg-background/80 backdrop-blur shadow-xl">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="tnf-display text-lg font-semibold">{t.pricing.calculator.title}</h3>
                    <button onClick={selectAll} className="text-xs font-medium px-3 py-1.5 rounded-full border hover:bg-secondary transition-colors">
                      {t.pricing.calculator.selectAll}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                    {MODULES_CONFIG.map(m => {
                      const checked = selectedModules.has(m.id)
                      return (
                        <label
                          key={m.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                            checked ? "border-primary/40 bg-primary/5" : "border-border hover:border-foreground/20"
                          }`}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggleModule(m.id)} className="sr-only" />
                          <div
                            className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                              checked ? "border-primary bg-primary" : "border-muted-foreground/30"
                            }`}
                          >
                            {checked && <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">{t.pricing.modules[m.id].name}</span>
                              {m.comingSoon && (
                                <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground flex-shrink-0">
                                  {t.pricing.soon}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-semibold flex-shrink-0 tabular-nums">{fmt(m.price)}</span>
                        </label>
                      )
                    })}
                  </div>

                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{t.pricing.calculator.extraLocations}</span>
                      <span className="text-xs text-muted-foreground">{fmt(LOCATION_PRICE)}{t.pricing.perMonthEach}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setExtraLocations(Math.max(0, extraLocations - 1))}
                        className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors"
                        aria-label="Decrease"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <div className="flex-1 bg-secondary/50 rounded-lg h-9 flex items-center justify-center">
                        <span className="text-sm font-semibold tabular-nums">{extraLocations}</span>
                      </div>
                      <button
                        onClick={() => setExtraLocations(extraLocations + 1)}
                        className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors"
                        aria-label="Increase"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.pricing.calculator.modules(selectedModules.size)}</span>
                      <span className="font-medium tabular-nums">{fmt(modulesTotal)}{t.pricing.perMonth}</span>
                    </div>
                    {extraLocations > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t.pricing.calculator.extraLocs(extraLocations)}</span>
                        <span className="font-medium tabular-nums">{fmt(locationsTotal)}{t.pricing.perMonth}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline pt-3 border-t">
                      <span className="font-semibold">{t.pricing.calculator.total}</span>
                      <span className="tnf-display text-3xl font-semibold tabular-nums text-primary">
                        {fmt(grandTotal)}<span className="text-xs font-medium text-muted-foreground ml-0.5">{t.pricing.perMonth}</span>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Reveal>

            <div className="text-center mt-12 space-y-4">
              <p className="text-sm text-muted-foreground">{t.pricing.cta.sub}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" asChild className="h-12 px-6 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/25">
                  <Link to="/signup" onClick={onTrialClick("pricing")} className="inline-flex items-center gap-2">
                    {t.pricing.cta.btn1}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="h-12 px-6 rounded-xl">
                  <a href={demoHref} onClick={onContactClick("pricing")}>{t.pricing.cta.btn2}</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="relative py-24 md:py-32 bg-secondary/30 border-y scroll-mt-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 max-w-6xl mx-auto">
              <Reveal className="lg:sticky lg:top-28 self-start space-y-5">
                <p className="tnf-eyebrow"><BrandMark size={7} />{t.faq.eyebrow}</p>
                <h2 className="tnf-display text-balance text-3xl md:text-[2.75rem] font-semibold leading-[1.1]">{t.faq.heading}</h2>
                <p className="text-lg text-muted-foreground">{t.faq.subheading}</p>
                <div className="rounded-2xl border bg-background p-5 flex items-center gap-4">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{t.faq.contact}</div>
                    <a href={demoHref} onClick={onContactClick("faq")} className="text-sm text-primary font-medium hover:underline">
                      {t.faq.contactCta} →
                    </a>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={120}>
                <Accordion type="single" collapsible defaultValue="faq-0" className="space-y-3">
                  {faqItems.map((item, i) => (
                    <AccordionItem key={item.q} value={`faq-${i}`} className="rounded-2xl border bg-background px-5 data-[state=open]:shadow-lg transition-shadow">
                      <AccordionTrigger className="text-left text-base font-semibold hover:no-underline py-5">{item.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-[15px] leading-relaxed pb-5">{item.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="container mx-auto px-4 py-24 md:py-28">
          <Reveal className="tnf-dark relative isolate overflow-hidden rounded-[2rem] bg-zinc-950 px-6 py-16 md:py-24 text-center text-white">
            <AuroraBackdrop className="opacity-80" />
            <div aria-hidden className="absolute inset-0 tnf-grid-dots text-white/[0.08]" />
            <div className="relative max-w-3xl mx-auto space-y-6">
              <div className="flex justify-center"><BrandMark size={12} /></div>
              <h2 className="tnf-display text-balance text-3xl md:text-5xl font-semibold leading-[1.08]">
                {t.cta.heading1}{" "}
                <span className="tnf-text-gradient-animated">{t.cta.heading2}</span>
              </h2>
              <p className="text-base md:text-lg text-white/70 leading-relaxed">
                {t.cta.description.split("\n").map((line, i) => (
                  <span key={i}>{line}{i === 0 && <br />}</span>
                ))}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Button size="lg" asChild className="h-12 px-6 rounded-xl text-base bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/30">
                  <Link to="/signup" onClick={onTrialClick("final_cta")} className="inline-flex items-center gap-2">
                    {t.cta.btn1}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="h-12 px-6 rounded-xl text-base bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white">
                  <a href={demoHref} onClick={onContactClick("final_cta")}>{t.cta.btn2}</a>
                </Button>
              </div>
              <div className="flex items-center justify-center gap-6 pt-4 text-sm text-white/60 flex-wrap">
                {[t.cta.check1, t.cta.check2, t.cta.check3].map((c, i) => (
                  <div key={c} className="flex items-center gap-1.5">
                    <Check className="w-4 h-4" strokeWidth={3} style={{ color: BRAND_COLORS[i] }} />
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background">
        <div className="container mx-auto px-4 py-14">
          <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-10 mb-10">
            <div className="space-y-4 max-w-sm">
              <Link to="/" aria-label="THE NOT FIT home">
                <Logo size="sm" />
              </Link>
              <p className="text-sm text-muted-foreground">{t.footer.tagline}</p>
              <div className="flex items-center gap-2 pt-1">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow THE NOT FIT on Instagram"
                  className="w-9 h-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.75} />
                </a>
                <a
                  href={`mailto:${SALES_EMAIL}`}
                  aria-label="Email THE NOT FIT"
                  className="w-9 h-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.footer.productHeading}</h4>
              <ul className="space-y-2 text-sm">
                {t.footer.productLinks.map(link => (
                  <li key={link.label}>
                    <a href={link.href} className="text-foreground/80 hover:text-foreground transition-colors">{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.footer.legalHeading}</h4>
              <ul className="space-y-2 text-sm">
                {t.footer.legalLinks.map(link => (
                  <li key={link.label}>
                    <Link to={link.href} className="text-foreground/80 hover:text-foreground transition-colors">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
            <p>{t.footer.copyright}</p>
            <LanguagePicker lang={lang} setLang={setLang} upward />
          </div>
        </div>
        <GrantFooterBand lang={lang} />
        {/* Room for the sticky mobile CTA */}
        <div className="h-20 md:hidden bg-[#0A0A0F]" />
      </footer>

      {/* Sticky mobile CTA */}
      <div
        aria-hidden={!showStickyCta}
        className={`fixed inset-x-0 bottom-0 z-40 md:hidden transition-transform duration-300 ${showStickyCta ? "translate-y-0" : "translate-y-[120%]"}`}
      >
        <div className="mx-3 mb-3 flex items-center gap-3 rounded-2xl border bg-background/90 p-2.5 pl-4 shadow-2xl backdrop-blur-xl">
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-[11px] font-medium uppercase tracking-wider text-primary truncate">{t.announcement.eyebrow}</div>
            <div className="text-sm font-semibold truncate">{fmt(OFFER_PRICE)}{t.pricing.offer.unit}</div>
          </div>
          <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/30">
            <Link to="/signup" tabIndex={showStickyCta ? 0 : -1} onClick={onTrialClick("sticky_mobile")}>
              {t.hero.cta1}
            </Link>
          </Button>
        </div>
      </div>

      <CookieConsent lang={lang} />
    </div>
  )
}
